// SVG → ball decal: parse vector outlines, nest closed shapes (inner ones punch
// holes in their parent and become their own panels), project onto the sphere.

const SELECTOR = 'path,polygon,polyline,rect,circle,ellipse,line';
const CLOSED_TAGS = new Set(['polygon', 'rect', 'circle', 'ellipse']);

const dist2 = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

function simplify(pts, eps) {
  if (pts.length < 3) return pts;
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop();
    const [ax, ay] = pts[a], [bx, by] = pts[b];
    const L = Math.hypot(bx - ax, by - ay) || 1e-12;
    let maxD = 0, idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs((bx - ax) * (ay - pts[i][1]) - (ax - pts[i][0]) * (by - ay)) / L;
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > eps) { keep[idx] = 1; stack.push([a, idx], [idx, b]); }
  }
  return pts.filter((_, i) => keep[i]);
}

// Closed loops: split at the point farthest from the start so both halves have distinct ends.
function simplifyClosed(pts, eps) {
  if (pts.length < 4) return pts;
  let far = 1, best = -1;
  pts.forEach((p, i) => { const d = dist2(p, pts[0]); if (d > best) { best = d; far = i; } });
  const a = simplify(pts.slice(0, far + 1), eps);
  const b = simplify([...pts.slice(far), pts[0]], eps);
  return [...a, ...b.slice(1, -1)];
}

export function signedArea2(pts) {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s / 2;
}

export function pointInPoly(p, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// Sample every geometry element into polylines in the SVG's own coordinates.
function sampleElements(svgEl) {
  const out = [];
  const els = svgEl.querySelectorAll(SELECTOR);
  let box = null;
  els.forEach((el) => {
    if (typeof el.getTotalLength !== 'function') return;
    let total = 0;
    try { total = el.getTotalLength(); } catch { return; }
    if (!(total > 0)) return;
    const ctm = el.getCTM();
    const n = Math.min(4000, Math.max(24, Math.ceil(total / 0.5)));
    const step = total / n;
    const raw = [];
    for (let i = 0; i <= n; i++) {
      let p = el.getPointAtLength(i * step);
      if (ctm) p = p.matrixTransform(ctm);
      raw.push([p.x, p.y]);
    }
    const tag = el.tagName.toLowerCase();
    const d = tag === 'path' ? el.getAttribute('d') || '' : '';
    out.push({ raw, step, tag, hasZ: /z/i.test(d), stepScaled: ctm ? step * Math.hypot(ctm.a, ctm.b) : step });
    raw.forEach(([x, y]) => {
      if (!box) box = [x, y, x, y];
      else { box[0] = Math.min(box[0], x); box[1] = Math.min(box[1], y); box[2] = Math.max(box[2], x); box[3] = Math.max(box[3], y); }
    });
  });
  return { items: out, box };
}

// Split a sampled element into sub-paths wherever consecutive samples jump.
function splitSubpaths(raw, gap) {
  const parts = [[raw[0]]];
  for (let i = 1; i < raw.length; i++) {
    if (dist2(raw[i], raw[i - 1]) > gap) parts.push([]);
    parts[parts.length - 1].push(raw[i]);
  }
  return parts.filter((p) => p.length >= 2);
}

// Returns { closed: [{outer, holes}], open: [[x,y]...] } normalised so the
// drawing's longest side spans [-0.5, 0.5], y pointing up.
export function parseSvgText(text) {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.querySelector('svg');
  if (!root || doc.querySelector('parsererror')) throw new Error('Not a valid SVG file');
  const host = document.createElement('div');
  host.style.cssText = 'position:absolute;left:-99999px;top:0;width:1px;height:1px;overflow:hidden;visibility:hidden';
  const svgEl = document.importNode(root, true);
  host.appendChild(svgEl);
  document.body.appendChild(host);
  let sampled;
  try { sampled = sampleElements(svgEl); } finally { host.remove(); }
  const { items, box } = sampled;
  if (!box) throw new Error('No vector shapes found in this SVG');
  const cx = (box[0] + box[2]) / 2, cy = (box[1] + box[3]) / 2;
  const span = Math.max(box[2] - box[0], box[3] - box[1]) || 1;
  const norm = (p) => [(p[0] - cx) / span, -(p[1] - cy) / span];
  const eps = 0.0015;
  const closedRings = [];
  const open = [];
  items.forEach(({ raw, stepScaled, tag, hasZ }) => {
    const gap = Math.max(stepScaled * 4, span * 0.004);
    splitSubpaths(raw, gap).forEach((part) => {
      const pts = part.map(norm);
      const ext = Math.max(...pts.map((p) => dist2(p, pts[0])));
      const closed = CLOSED_TAGS.has(tag) || (tag === 'path' && hasZ) || dist2(pts[0], pts[pts.length - 1]) < Math.max(0.004, ext * 0.03);
      if (closed) {
        let ring = pts;
        if (dist2(ring[0], ring[ring.length - 1]) < 1e-6) ring = ring.slice(0, -1);
        ring = simplifyClosed(ring, eps);
        if (ring.length >= 3 && Math.abs(signedArea2(ring)) > 1e-6) {
          if (signedArea2(ring) < 0) ring.reverse();
          closedRings.push(ring);
        }
      } else {
        const line = simplify(pts, eps);
        if (line.length >= 2) open.push(line);
      }
    });
  });
  return { closed: nestRings(closedRings), open };
}

// Each ring becomes a panel; rings directly inside it become its holes.
export function nestRings(rings) {
  const order = rings
    .map((r, i) => ({ r, i, a: Math.abs(signedArea2(r)) }))
    .sort((x, y) => y.a - x.a);
  const parent = new Array(order.length).fill(-1);
  for (let k = 0; k < order.length; k++) {
    for (let j = k - 1; j >= 0; j--) {
      if (order[j].a > order[k].a && pointInPoly(order[k].r[0], order[j].r)) { parent[k] = j; break; }
    }
  }
  return order.map((o, k) => ({
    outer: o.r,
    holes: order.filter((_, c) => parent[c] === k).map((c) => c.r.slice().reverse()),
  }));
}

// Subdivide segments so projected lines follow the ball instead of cutting chords.
export function densify2d(pts, closed, maxStep = 0.01) {
  const out = [];
  const n = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const k = Math.max(1, Math.ceil(dist2(a, b) / maxStep));
    for (let j = 0; j < k; j++) out.push([a[0] + ((b[0] - a[0]) * j) / k, a[1] + ((b[1] - a[1]) * j) / k]);
  }
  if (closed) out.push(pts[0]);
  else out.push(pts[pts.length - 1]);
  return out;
}

// Map normalised 2D points onto the ball around `center` (unit vector).
export function projectDecal(pts, { center, sizeRad, spinRad = 0 }) {
  const c = center;
  const up = Math.abs(c[1]) < 0.95 ? [0, 1, 0] : [1, 0, 0];
  let ex = [up[1] * c[2] - up[2] * c[1], up[2] * c[0] - up[0] * c[2], up[0] * c[1] - up[1] * c[0]];
  const el = Math.hypot(...ex) || 1;
  ex = ex.map((v) => v / el);
  const ey = [c[1] * ex[2] - c[2] * ex[1], c[2] * ex[0] - c[0] * ex[2], c[0] * ex[1] - c[1] * ex[0]];
  const cs = Math.cos(spinRad), sn = Math.sin(spinRad);
  return pts.map(([u0, v0]) => {
    const u = u0 * cs - v0 * sn, v = u0 * sn + v0 * cs;
    const r = Math.hypot(u, v);
    if (r < 1e-12) return c.slice();
    const d = r * sizeRad, s = Math.sin(d) / r, co = Math.cos(d);
    const p = [0, 1, 2].map((i) => co * c[i] + s * (u * ex[i] + v * ey[i]));
    const l = Math.hypot(...p);
    return p.map((x) => x / l);
  });
}
